/**
 * Three tabs, shaped like the factory's work rather than like our filing.
 *
 * ── What was here before ───────────────────────────────────────────────────
 *
 * Eight tabs became three: Specs & Proofing, Messages, Production & Logistics.
 * That was an improvement in count and a mistake in kind. Those three are OUR
 * internal categories — the departments a job passes through at TAP — and a
 * factory does not have a specs department. It has jobs.
 *
 * The result was that one job's PO, artwork, samples and shipping details sat
 * on two different tabs with two other jobs' details interleaved between them,
 * and the thing a factory most needed — what am I making, and what do I do
 * next — was on none of them.
 *
 *   Jobs        every job order, on a board, in the stage it is actually in
 *   Inventory   what materials they are holding
 *   Messages    what has been said, and what TAP has shared
 *
 * ── Why Jobs is a board ────────────────────────────────────────────────────
 *
 * Because "which of my four jobs is waiting on me" is a question about all of
 * them at once, and a list answers it one row at a time. The nine columns are
 * the same nine steps the stage bar shows inside a job, so the board and the
 * job agree about what a stage is called.
 */

export type PortalTab = 'jobs' | 'inventory' | 'messages';

export const PORTAL_TABS: { id: PortalTab; label: string }[] = [
  { id: 'jobs', label: 'Jobs' },
  { id: 'inventory', label: 'Inventory' },
  { id: 'messages', label: 'Messages' },
];

/**
 * The older tab names, and where their content now lives.
 *
 * Kept as data rather than dropped, for the same reason the eight were: every
 * one of these appears in a link TAP has already sent somebody.
 */
export const PORTAL_TAB_SOURCES: Record<PortalTab, string[]> = {
  jobs: ['Active Orders', 'Action Required', 'Send Samples', 'From TAP', 'Completed'],
  inventory: ['Materials Stock'],
  messages: ['Messages', 'Shared Reviews', 'Files for Review'],
};

export const DEFAULT_PORTAL_TAB: PortalTab = 'jobs';

function isPortalTab(value: string): value is PortalTab {
  return PORTAL_TABS.some((tab) => tab.id === value);
}

/**
 * Which tab a URL is asking for.
 *
 * Unknown values fall back rather than rendering nothing. A link in an email
 * from six months ago pointing at `?tab=files` should open the portal, not a
 * blank panel — and the old names are exactly the links that exist in the
 * wild.
 */
export function tabFromParams(params: URLSearchParams): PortalTab {
  const asked = params.get('tab') ?? '';
  if (isPortalTab(asked)) {
    return asked;
  }
  return LEGACY_TABS[asked] ?? DEFAULT_PORTAL_TAB;
}

/**
 * Every id this portal has ever used, redirected to where its content lives.
 *
 * Two generations of them now: the original eight, and the three that replaced
 * them. `specs` and `logistics` were live links for weeks — dropping them
 * would turn every notification email sent in that window into a dead end.
 */
const LEGACY_TABS: Record<string, PortalTab> = {
  // The original eight.
  action: 'jobs',
  orders: 'jobs',
  submit: 'jobs',
  fromtap: 'jobs',
  reviews: 'messages',
  files: 'messages',
  completed: 'jobs',
  // The three they became.
  specs: 'jobs',
  logistics: 'jobs',
};

/** The order a URL is asking for, or null for the whole board. */
export function orderFromParams(params: URLSearchParams): string | null {
  const asked = (params.get('order') ?? '').trim();
  return asked === '' ? null : asked;
}

/** A deep link into one order's tab. */
export function portalHref(orderId: string | null, tab: PortalTab): string {
  const params = new URLSearchParams();
  if (orderId !== null && orderId !== '') {
    params.set('order', orderId);
  }
  params.set('tab', tab);
  return `/supplier?${params.toString()}`;
}
